import { useState, useCallback } from 'react';
import { DayJs } from './Utils';
import { useBusinessContext } from '../context/BusinessContext';

export const useCustomRates = () => {
  const business = useBusinessContext();
  const [customRates, setCustomRates] = useState<{ [key: string]: number }>({});

  const clearCustomRates = useCallback(() => {
    setCustomRates({});
  }, [setCustomRates]);

  const getCustomRate = useCallback(
    (
      date: Date,
      sourceCurrency?: string,
      targetCurrency?: string,
    ): number | undefined => {
      if (!sourceCurrency || !targetCurrency) {
        return undefined;
      }
      const key = `${DayJs(date).format('YYYY-MM-DD')}-${sourceCurrency}-${targetCurrency}`;
      return customRates[key];
    },
    [customRates],
  );

  const setCustomRate = useCallback(
    (
      date: Date,
      sourceCurrency?: string,
      targetCurrency?: string,
      rate?: number,
      override?: boolean,
      clearAll?: boolean,
    ) => {
      if (!sourceCurrency || !targetCurrency || !rate) {
        return;
      }

      const key = `${DayJs(date).format('YYYY-MM-DD')}-${sourceCurrency}-${targetCurrency}`;
      if (clearAll) {
        setCustomRates({ [key]: rate });
      } else {
        setCustomRates((prev) => {
          if (override) {
            return { ...prev, [key]: rate };
          } else if (!prev[key]) {
            return { ...prev, [key]: rate };
          }
          return prev;
        });
      }
    },
    [setCustomRates],
  );

  const hasRateChanged = useCallback(
    (date: Date, srcCcy?: string, targetCurrency?: string): boolean => {
      if (!srcCcy || !targetCurrency) return false;
      const rate = business.getCachedCurrencyRate(
        srcCcy,
        targetCurrency,
        date,
      )?.rate;
      const customRate = getCustomRate(date, srcCcy, targetCurrency) ?? 0;
      return customRate !== rate;
    },
    [business, getCustomRate],
  );

  return {
    clearCustomRates,
    getCustomRate,
    setCustomRate,
    hasRateChanged,
  };
};
